import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MobileNavigation } from '../components/MobileNavigation';
import { Users, CheckCircle, Clock, Warning, User, Phone, FirstAid, ArrowsLeftRight } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
export function StaffRoster() {
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [roster, setRoster] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [userRole, setUserRole] = useState('staff');
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [movePlayer, setMovePlayer] = useState(null);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [loadingMove, setLoadingMove] = useState(false);
  
  useEffect(() => {
    loadSessions();
    checkUserRole();
  }, []);
  
  useEffect(() => {
    if (selectedSession) {
      loadRosterAndWaitlist(selectedSession);
    }
  }, [selectedSession]);
  
  const checkUserRole = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUserRole(data.role);
      }
    } catch (error) {
      console.error('Check role error:', error);
    }
  };
  
  const loadSessions = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/staff/sessions`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
        
        // Auto-select today's first session
        const today = new Date().toISOString().split('T')[0];
        const todaySession = data.find(s => s.date === today);
        if (todaySession) {
          setSelectedSession(todaySession.session_id);
        } else if (data.length > 0) {
          setSelectedSession(data[0].session_id);
        }
      }
    } catch (error) {
      console.error('Load sessions error:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };
  
  const loadRosterAndWaitlist = async (sessionId) => {
    setLoadingRoster(true);
    try {
      const [rosterRes, waitlistRes] = await Promise.all([
        fetch(`${getApiBaseUrl()}/api/sessions/${sessionId}/roster`, {
          credentials: 'include'
        }),
        fetch(`${getApiBaseUrl()}/api/sessions/${sessionId}/waitlist`, {
          credentials: 'include'
        })
      ]);
      
      if (rosterRes.ok) {
        const rosterData = await rosterRes.json();
        // Handle both formats: {roster: [...]} or just [...]
        setRoster(rosterData.roster || rosterData || []);
      }
      
      if (waitlistRes.ok) {
        const waitlistData = await waitlistRes.json();
        // Handle both formats: {waitlist: [...]} or just [...]
        setWaitlist(waitlistData.waitlist || waitlistData || []);
      }
    } catch (error) {
      console.error('Load roster error:', error);
      toast.error('Failed to load roster');
    } finally {
      setLoadingRoster(false);
    }
  };
  
  const handleManualCheckin = async (playerId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/staff/checkin/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          player_id: playerId,
          session_id: selectedSession
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.player_name} checked in!`);
        loadRosterAndWaitlist(selectedSession);
      } else {
        const error = await response.json();
        // Show detailed error message
        toast.error(error.detail || 'Check-in failed', { duration: 5000 });
      }
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error('Check-in failed');
    }
  };
  
  const handleOpenMoveDialog = async (player) => {
    setMovePlayer(player);
    setLoadingMove(true);
    setShowMoveDialog(true);
    
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/sessions/available-for-transfer/${player.player_id}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAvailableSessions(data.available_sessions);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to load available sessions');
      }
    } catch (error) {
      console.error('Load available sessions error:', error);
      toast.error('Failed to load available sessions');
    } finally {
      setLoadingMove(false);
    }
  };
  
  const handleMovePlayer = async (targetSessionId) => {
    if (!movePlayer || !selectedSession) return;
    
    setLoadingMove(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/registrations/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          player_id: movePlayer.player_id,
          from_session_id: selectedSession,
          to_session_id: targetSessionId,
          reason: 'Admin transfer'
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowMoveDialog(false);
        setMovePlayer(null);
        loadRosterAndWaitlist(selectedSession);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to move player');
      }
    } catch (error) {
      console.error('Move player error:', error);
      toast.error('Failed to move player');
    } finally {
      setLoadingMove(false);
    }
  };
  
  const handlePromoteWaitlist = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/sessions/${selectedSession}/promote-waitlist`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.player_name} promoted from waitlist!`);
        loadRosterAndWaitlist(selectedSession);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Promotion failed');
      }
    } catch (error) {
      console.error('Promote error:', error);
      toast.error('Promotion failed');
    }
  };
  
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const time = new Date();
    time.setHours(parseInt(hours), parseInt(minutes));
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const selectedSessionData = sessions.find(s => s.session_id === selectedSession);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="staff-roster">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <h1 className="text-xl font-heading">SESSION ROSTER</h1>
          <p className="text-sm text-muted-foreground">
            View and manage player attendance
          </p>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Session Selection */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg">SELECT SESSION</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedSession} onValueChange={setSelectedSession}>
              <SelectTrigger data-testid="session-select">
                <SelectValue placeholder="Choose a session" />
              </SelectTrigger>
              <SelectContent>
                {sessions.map((session) => (
                  <SelectItem key={session.session_id} value={session.session_id}>
                    {formatDate(session.date)} • {formatTime(session.start_time)} - {session.location?.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedSessionData && (
              <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-muted/20 rounded-lg">
                  <p className="text-2xl font-bold text-primary">{selectedSessionData.registered_count}</p>
                  <p className="text-xs text-muted-foreground">Registered</p>
                </div>
                <div className="p-3 bg-muted/20 rounded-lg">
                  <p className="text-2xl font-bold text-green-500">{selectedSessionData.checked_in_count}</p>
                  <p className="text-xs text-muted-foreground">Checked In</p>
                </div>
                <div className="p-3 bg-muted/20 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-500">{selectedSessionData.waitlist_count}</p>
                  <p className="text-xs text-muted-foreground">Waitlist</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Roster */}
        {selectedSession && (
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">REGISTERED PLAYERS</CardTitle>
              <Badge variant="outline" className="ml-2">
                {roster.length} / {selectedSessionData?.capacity || 25}
              </Badge>
            </CardHeader>
            <CardContent>
              {loadingRoster ? (
                <div className="py-8 text-center">
                  <LoadingSpinner />
                </div>
              ) : roster.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <Users size={48} weight="duotone" className="mx-auto mb-4 opacity-50" />
                  <p>No players registered yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {roster.map((item) => (
                    <div
                      key={item.registration.registration_id}
                      className={`p-4 rounded-lg border ${
                        item.checked_in 
                          ? 'border-green-500/30 bg-green-500/10' 
                          : 'border-border bg-muted/20'
                      }`}
                      data-testid={`roster-item-${item.player?.player_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            item.checked_in ? 'bg-green-500' : 'bg-muted'
                          }`}>
                            {item.checked_in ? (
                              <CheckCircle size={20} weight="bold" className="text-white" />
                            ) : (
                              <User size={20} weight="bold" className="text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{item.player?.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Age {item.player?.age} • {item.registration.payment_status === 'paid' ? 'Paid' : 'Payment Pending'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Admin: Move button */}
                          {userRole === 'admin' && !item.checked_in && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenMoveDialog(item.player)}
                              className="text-muted-foreground hover:text-primary"
                              title="Move to different session"
                            >
                              <ArrowsLeftRight size={16} />
                            </Button>
                          )}
                          
                          {item.checked_in ? (
                            <Badge className="bg-green-500 text-white">
                              Checked In
                            </Badge>
                          ) : item.registration.payment_status === 'paid' ? (
                            <Button
                              size="sm"
                              onClick={() => handleManualCheckin(item.player?.player_id)}
                              className="btn-primary"
                              data-testid={`checkin-btn-${item.player?.player_id}`}
                            >
                              Check In
                            </Button>
                          ) : (
                            <Badge variant="outline" className="text-yellow-500 border-yellow-500">
                              Unpaid
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Emergency Contact */}
                      {item.player?.emergency_contact_name && (
                        <div className="mt-2 pt-2 border-t border-border/50 flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Phone size={12} />
                            <span>{item.player.emergency_contact_name}: {item.player.emergency_contact_phone}</span>
                          </div>
                          {item.player.medical_notes && (
                            <div className="flex items-center gap-1 text-yellow-500">
                              <FirstAid size={12} />
                              <span>Medical notes</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Move Player Dialog (Admin Only) */}
        <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
          <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">MOVE PLAYER</DialogTitle>
            </DialogHeader>
            {movePlayer && (
              <div className="space-y-4">
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="font-medium">{movePlayer.name}</p>
                  <p className="text-sm text-muted-foreground">Age {movePlayer.age}</p>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Select a session to transfer this player to:
                </p>
                
                {loadingMove ? (
                  <div className="py-8 text-center">
                    <LoadingSpinner />
                  </div>
                ) : availableSessions.length === 0 ? (
                  <div className="py-4 text-center text-muted-foreground">
                    <p>No available sessions match this player's age group.</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {availableSessions.map((session) => (
                      <button
                        key={session.session_id}
                        onClick={() => handleMovePlayer(session.session_id)}
                        className="w-full p-3 text-left rounded-lg border border-border hover:border-primary hover:bg-primary/10 transition-colors"
                      >
                        <p className="font-medium">
                          {new Date(session.date + 'T00:00:00').toLocaleDateString('en-US', { 
                            weekday: 'short', month: 'short', day: 'numeric' 
                          })}
                          {' • '}
                          {session.start_time}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {session.location_name} • {session.spots_available} spots left
                        </p>
                      </button>
                    ))}
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  onClick={() => setShowMoveDialog(false)} 
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Waitlist */}
        {selectedSession && waitlist.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Clock size={20} weight="duotone" className="text-yellow-500" />
                WAITLIST
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={handlePromoteWaitlist}
                data-testid="promote-waitlist-btn"
              >
                Promote Next
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {waitlist.map((item) => (
                  <div
                    key={item.registration.registration_id}
                    className="p-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 flex items-center justify-between"
                    data-testid={`waitlist-item-${item.player?.player_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center text-white font-bold text-sm">
                        {item.position}
                      </div>
                      <div>
                        <p className="font-medium">{item.player?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Age {item.player?.age} • {item.parent_name}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-yellow-500 border-yellow-500">
                      #{item.position}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      <MobileNavigation userRole="staff" />
    </div>
  );
}
